import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { SharedKey } from "../../../../Global/Base/Utils/EventUtils";
import { setupBem } from "../../../../Global/Dom/bem";
import { EventsController } from "../../../../Global/Dom/EventsController";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";
import { MessageButtonType, MessageType, MessageWindow } from "../MessageWindow";
import { Button, ButtonType } from "../XGControls/Actions/Button";

const bemClass = setupBem("component-expander");
const buttonSmallClass: string = bemClass.element("expand-button", "small");
const buttonStandardClass: string = bemClass.element("expand-button", "standard");
const buttonInvisibleClass: string = bemClass.element("expand-button", "invisible");

export class ComponentExpander {
    public onKeyClicked: GlobalEvent<ComponentExpander, KeyboardEvent> = new GlobalEvent<ComponentExpander, KeyboardEvent>();

    private _expandButton: HTMLDivElement = new Button("", { iconText: "", onLeft: false }, ButtonType.flat).element;
    private _messageWindow: MessageWindow;
    private _expandedComponent: any;
    private _componentMode: ComponentExpandButtonMode;
    private _expandedComponentElementRefence: HTMLElement;
    private _windowTitle: string = "";
    private _onButtonShow: () => boolean = () => {
        return true;
    };
    private _doNotHideExpandButton: boolean = true;
    private _eventController: EventsController;

    public set onButtonShow(onShow: () => boolean) {
        this._onButtonShow = onShow;
    }

    public get messageWindow(): MessageWindow {
        return this._messageWindow;
    }

    //#region Constructors
    constructor(expandedComponent: any, windowTitle: string = null, buttonRenderElement: HTMLElement = expandedComponent.element) {
        this._expandedComponent = expandedComponent;
        this._windowTitle = windowTitle;
        buttonRenderElement.appendChild(this._expandButton);
        this._expandButton.tabIndex = 1;
        this._eventController = new EventsController();
        this._eventController.addListener(this._expandButton, "click", () => {
            if (this._componentMode === ComponentExpandButtonMode.Open) {
                this._expand(this._expandedComponent.focus);
            } else {
                this.closeExpandedComponent();
            }
        });
        this._expandButton.classList.add("icon", bemClass.element("expand-button"), buttonInvisibleClass);

        const expanderButtonPXSize = parseInt(getComputedStyle(document.body).getPropertyValue("--base-3")) +
                                    (parseInt(getComputedStyle(document.body).getPropertyValue("--base-h")) * 2);
        this._eventController.addListener(this._expandedComponent.element, "mouseover", () => {
            if (this._expandedComponent.element.offsetHeight < expanderButtonPXSize) {
                if (!this._expandButton.classList.contains(buttonSmallClass)) {
                    this._expandButton.classList.add(buttonSmallClass);
                    this._expandButton.classList.remove(buttonStandardClass);
                }
            } else if (!this._expandButton.classList.contains(buttonStandardClass)) {
                this._expandButton.classList.add(buttonStandardClass);
                this._expandButton.classList.remove(buttonSmallClass);
            }

            if (this._onButtonShow()) {
                this._expandButton.classList.toggle(buttonInvisibleClass, false);
            }
        });

        this._eventController.addListener(this._expandedComponent.element, "mouseleave", () => {
            if (!this._doNotHideExpandButton) {
                this._expandButton.classList.toggle(buttonInvisibleClass, true);
            }
        });
        this._setExpandButtonMode(ComponentExpandButtonMode.Open);
        this.onKeyClicked.add((sender: ComponentExpander, ev: KeyboardEvent) => {
            this._keyClicked(sender, ev);
        });
    }
    //#endregion Constructors
    //#region Methods
    private _keyClicked(sender: ComponentExpander, ev: KeyboardEvent): void {
        if (ev.key === SharedKey.Escape) {
            sender.closeExpandedComponent();
        }
    }

    private _expand(onExpandCallback?: () => void): void {
        this._expandedComponentElementRefence = createElement({ type: "div" }) as HTMLDivElement;
        this._expandedComponent.element.parentNode.insertBefore(this._expandedComponentElementRefence, this._expandedComponent.element);
        this._setExpandButtonMode(ComponentExpandButtonMode.Close);
        this._expandedComponent.element.classList.add(bemClass.modifier("expanded"));
        this._messageWindow = new MessageWindow(new ComponentExpanderBuilder(this._expandedComponent.element), [bemClass()]);
        this._messageWindow.focusElement = this._expandedComponent;
        this._messageWindow.header = this._windowTitle || this._expandedComponent.locKey;
        this._messageWindow.type = MessageType.empty;
        this._messageWindow.show().then(onExpandCallback);
        this._messageWindow.element.onkeyup = (ev: KeyboardEvent) => {
            ev.stopPropagation();
            this._keyClicked(this, ev);
        };
    }

    private _setExpandButtonMode(mode: ComponentExpandButtonMode): void {
        this._componentMode = mode;
        this._expandButton.classList.remove(bemClass.element("expand-button", "open"));
        if (mode === ComponentExpandButtonMode.Close)
            this._expandButton.classList.add(bemClass.element("expand-button", "open"));
        this._doNotHideExpandButton = mode === ComponentExpandButtonMode.Close;
    }

    public closeExpandedComponent(): void {
        if (!this.isClosed()) {
            this._expandedComponent.element.classList.remove(bemClass.modifier("expanded"));
            this._expandedComponentElementRefence.replaceWith(this._expandedComponent.element);
            this._messageWindow.element.onkeyup = null;
            this._messageWindow.close(MessageButtonType.other);
            delete this._expandedComponentElementRefence;
            this._setExpandButtonMode(ComponentExpandButtonMode.Open);
        }
    }

    public isClosed(): boolean {
        return this._expandedComponentElementRefence ? false : true;
    }

    public dispose(): void {
        this._eventController.removeAllListeners();
        this.onKeyClicked.removeAll();
    }
}

const enum ComponentExpandButtonMode {
    Close = "ui-shrink",
    Open = "ui-enlarge",
}

class ComponentExpanderBuilder implements IContentBuilder {
    private _contentElement: HTMLElement;
    constructor(element: HTMLElement) {
        this._contentElement = element;
    }
    public async build(): Promise<HTMLElement> {
        return this._contentElement;
    }
}
